import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Dices,
  Sparkles,
  Lock,
  Unlock,
  RotateCcw,
  Bookmark,
  Trash2,
  FileText,
  FolderOpen,
  Check,
  Copy,
  Pencil,
  SlidersHorizontal,
  Hash,
  Download,
  ClipboardList,
  Eraser,
  KeyRound,
  ChevronLeft,
} from "lucide-react";
import JamSlotPicker from "@/components/JamSlotPicker";
import {
  JAM_SLOT_LABELS,
  rollName,
  rollFrom,
  buildPitch,
  conceptSummary,
  twistHint,
  encodeConcept,
  decodeConcept,
  conceptsToText,
  type JamConcept,
  type JamSlotKey,
} from "@/content/jam";
import { useJamStore } from "@/store/jamStore";
import { useJamSelectionStore } from "@/store/jamSelectionStore";
import { useConceptAxes } from "@/store/customContentStore";
import { useDesignDocStore } from "@/store/designDocStore";
import { prefillFromConcept } from "@/core/docPrefill";

export default function Jam() {
  const navigate = useNavigate();
  const createDoc = useDesignDocStore((s) => s.create);
  const { favorites, save, remove } = useJamStore();
  const activeAxes = useJamSelectionStore((s) => s.activeAxisCount());
  const conceptAxes = useConceptAxes();
  const slotLabels: Record<string, string> = {
    twist: JAM_SLOT_LABELS.twist,
    ...Object.fromEntries(conceptAxes.map((a) => [a.key, a.label])),
  };

  const [started, setStarted] = useState(false);
  const [concept, setConcept] = useState<JamConcept | null>(null);
  const [lockedSlots, setLockedSlots] = useState<Set<JamSlotKey>>(new Set());
  const [titleLocked, setTitleLocked] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [rolling, setRolling] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showSeed, setShowSeed] = useState(false);
  const [seedInput, setSeedInput] = useState("");
  const [toast, setToast] = useState("");
  const interval = useRef<number | null>(null);
  const toastTimer = useRef<number | null>(null);

  useEffect(() => () => {
    if (interval.current) window.clearInterval(interval.current);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
  }, []);

  function flashToast(msg: string) {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 1800);
  }

  function reroll(base: JamConcept | null, opts?: { locks?: Set<JamSlotKey>; titleLocked?: boolean }) {
    if (rolling) return;
    const locks = opts?.locks ?? lockedSlots;
    const tLock = opts?.titleLocked ?? titleLocked;
    const selection = useJamSelectionStore.getState().activeSelection();
    const activeKeys = Object.keys(selection);

    const seedTitle = tLock && base?.title ? base.title : rollName();
    const final: JamConcept = { title: seedTitle };
    for (const k of activeKeys) {
      if (locks.has(k) && base && base[k]) final[k] = base[k]!;
      else final[k] = rollFrom(selection[k]!);
    }

    setRolling(true);
    setToast("");
    let ticks = 0;
    if (interval.current) window.clearInterval(interval.current);
    interval.current = window.setInterval(() => {
      ticks++;
      setConcept((cur) => {
        const c = cur ?? final;
        const n: JamConcept = { title: tLock ? c.title ?? seedTitle : rollName() };
        for (const k of activeKeys) n[k] = locks.has(k) ? final[k] : rollFrom(selection[k]!);
        return n;
      });
      if (ticks >= 14) {
        if (interval.current) window.clearInterval(interval.current);
        setConcept(final);
        setRolling(false);
      }
    }, 60);
  }

  function startFresh() {
    setStarted(true);
    setEditingName(false);
    const empty = new Set<JamSlotKey>();
    setLockedSlots(empty);
    setTitleLocked(false);
    reroll(null, { locks: empty, titleLocked: false });
  }
  function openTwistMode() {
    useJamSelectionStore.getState().onlyAxis("twist");
    startFresh();
  }
  function fullConceptMode() {
    useJamSelectionStore.getState().enableAll();
    startFresh();
  }

  const activeKeys = concept ? Object.keys(concept).filter((k) => k !== "title") : [];
  const conceptKeys = activeKeys.filter((k) => k !== "twist");
  const openMode = conceptKeys.length === 0 && Boolean(concept?.twist);
  const allLocked = activeKeys.length > 0 && activeKeys.every((k) => lockedSlots.has(k)) && titleLocked;

  function toggleLock(key: JamSlotKey) {
    setLockedSlots((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function clearSlot(key: JamSlotKey) {
    setConcept((c) => {
      if (!c) return c;
      const n = { ...c };
      delete n[key];
      return n;
    });
    setLockedSlots((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }
  function lockAll() {
    setLockedSlots(new Set(activeKeys));
    setTitleLocked(true);
  }
  function unlockAll() {
    setLockedSlots(new Set());
    setTitleLocked(false);
  }
  function rerollName() {
    if (titleLocked) return;
    setConcept((c) => (c ? { ...c, title: rollName() } : c));
  }
  function saveIdea() {
    if (!concept) return;
    flashToast(save(concept) ? "Saved!" : "Already saved");
  }
  function createPitch() {
    if (!concept) return;
    const { title, values } = prefillFromConcept(concept);
    const id = createDoc("pitch", { title, values });
    navigate("/docs", { state: { openDocId: id } });
  }
  function copyPitch() {
    if (!concept) return;
    navigator.clipboard?.writeText(buildPitch(concept)).then(
      () => flashToast("Pitch copied"),
      () => flashToast("Copy failed")
    );
  }
  function copySeed() {
    if (!concept) return;
    navigator.clipboard?.writeText(encodeConcept(concept)).then(
      () => flashToast("Seed copied"),
      () => flashToast("Copy failed")
    );
  }
  function loadSeed() {
    const c = decodeConcept(seedInput);
    if (!c) {
      flashToast("Invalid seed");
      return;
    }
    loadConcept(c);
    setSeedInput("");
    setShowSeed(false);
    flashToast("Seed loaded");
  }
  function loadConcept(c: JamConcept) {
    setConcept(c);
    setStarted(true);
    setLockedSlots(new Set());
    setTitleLocked(false);
    setEditingName(false);
  }
  function copyAllFavorites() {
    navigator.clipboard?.writeText(conceptsToText(favorites.map((f) => f.concept))).then(
      () => flashToast("All ideas copied"),
      () => flashToast("Copy failed")
    );
  }
  function exportFavorites() {
    const blob = new Blob([conceptsToText(favorites.map((f) => f.concept))], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hippo-game-jam-ideas.txt";
    a.click();
    URL.revokeObjectURL(url);
    flashToast("Exported .txt");
  }

  const toastEl = toast ? (
    <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg shadow-glow">
      <span className="inline-flex items-center gap-1.5">
        <Check className="h-4 w-4" /> {toast}
      </span>
    </div>
  ) : null;

  // ---- Idle ----
  if (!started || !concept) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="grid h-16 w-16 place-items-center rounded-2xl bg-surface-2 text-primary">
            <Dices className="h-8 w-8" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-text">Spark a game-jam idea</h2>
            <p className="mt-2 text-muted">
              Get an <span className="text-text">open theme</span> to interpret freely, or a{" "}
              <span className="text-text">full guided concept</span>. Lock what you love and reroll the rest.
            </p>
          </div>

          <div className="grid w-full gap-3 sm:grid-cols-2">
            <button
              onClick={openTwistMode}
              className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-surface p-6 transition-all hover:-translate-y-0.5 hover:border-primary/50"
            >
              <Sparkles className="h-7 w-7 text-accent" />
              <span className="font-display font-semibold text-text">Open Twist</span>
              <span className="text-xs text-muted">Just a jam theme — total freedom.</span>
            </button>
            <button
              onClick={fullConceptMode}
              className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-surface p-6 shadow-glow transition-all hover:-translate-y-0.5 hover:border-primary/50"
            >
              <Dices className="h-7 w-7 text-primary" />
              <span className="font-display font-semibold text-text">Full Concept</span>
              <span className="text-xs text-muted">Theme + genre + mechanic + more.</span>
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setShowCustomize((v) => !v)}
              className={toolBtn(showCustomize)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Customize roll ({activeAxes} slots)
            </button>
            <button onClick={() => setShowSeed((v) => !v)} className={toolBtn(showSeed)}>
              <KeyRound className="h-4 w-4" />
              Load from seed
            </button>
          </div>

          {showSeed && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex w-full max-w-md gap-2">
              <input
                value={seedInput}
                onChange={(e) => setSeedInput(e.target.value)}
                placeholder="Paste a seed code…"
                className="flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none"
              />
              <button onClick={loadSeed} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg">
                Load
              </button>
            </motion.div>
          )}

          {showCustomize && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="w-full rounded-2xl border border-border bg-surface p-6 text-left">
              <p className="mb-4 text-sm text-muted">
                Choose which slots and values can roll. Turn everything off except{" "}
                <span className="text-text">Twist / Theme</span> for an open prompt.
              </p>
              <JamSlotPicker />
              <button
                onClick={startFresh}
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]"
              >
                <Dices className="h-4 w-4" />
                Generate with these
              </button>
            </motion.div>
          )}
        </div>

        {favorites.length > 0 && (
          <Favorites favorites={favorites} onLoad={loadConcept} onRemove={remove} onCopyAll={copyAllFavorites} onExport={exportFavorites} />
        )}
        {toastEl}
      </div>
    );
  }

  // ---- Board ----
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <button
        onClick={() => setStarted(false)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted transition-colors hover:text-text"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      {openMode ? (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-gradient-to-br from-surface to-surface-2 p-8 text-center shadow-glow">
          <div className="text-xs font-semibold uppercase tracking-widest text-accent">Game-jam theme</div>
          <div className="mt-2 flex items-center justify-center gap-3">
            <h2 className={`font-display text-3xl font-bold ${rolling ? "text-primary/70" : "text-text"}`}>{concept.twist}</h2>
            <button
              onClick={() => toggleLock("twist")}
              className={lockBtn(lockedSlots.has("twist"))}
              aria-label="Lock theme"
            >
              {lockedSlots.has("twist") ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
            </button>
          </div>
          {twistHint(concept.twist!) && <p className="mx-auto mt-3 max-w-xl text-sm text-muted">{twistHint(concept.twist!)}</p>}
        </motion.div>
      ) : (
        <>
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-gradient-to-br from-surface to-surface-2 p-7 text-center shadow-glow">
            <div className="text-xs font-semibold uppercase tracking-widest text-accent">Concept codename</div>
            <div className="mt-1 flex items-center justify-center gap-2">
              {editingName ? (
                <input
                  value={concept.title}
                  autoFocus
                  onChange={(e) => setConcept((c) => (c ? { ...c, title: e.target.value } : c))}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => e.key === "Enter" && setEditingName(false)}
                  className="w-full max-w-sm rounded-lg border border-border bg-bg px-3 py-1.5 text-center font-display text-2xl font-bold text-text focus:border-primary focus:outline-none"
                />
              ) : (
                <h2 className={`font-display text-3xl font-bold ${rolling ? "text-primary/70" : "text-text"}`}>{concept.title}</h2>
              )}
              <NameBtn onClick={() => setEditingName((v) => !v)} label="Edit name"><Pencil className="h-3.5 w-3.5" /></NameBtn>
              <NameBtn onClick={rerollName} label="Reroll name" disabled={titleLocked}><RotateCcw className="h-3.5 w-3.5" /></NameBtn>
              <NameBtn onClick={() => setTitleLocked((v) => !v)} label="Lock name" active={titleLocked}>
                {titleLocked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
              </NameBtn>
            </div>
            <p className="mx-auto mt-3 max-w-xl text-sm text-muted">{buildPitch(concept)}</p>
          </motion.div>

          <div className="grid gap-3 sm:grid-cols-2">
            {activeKeys.map((key) => {
              const isLocked = lockedSlots.has(key);
              const spinning = rolling && !isLocked;
              return (
                <div
                  key={key}
                  className={[
                    "flex items-center justify-between gap-2 rounded-xl border px-4 py-3 transition-colors",
                    isLocked ? "border-primary/60 bg-primary/5" : "border-border bg-surface",
                  ].join(" ")}
                >
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-muted">{slotLabels[key] ?? key}</div>
                    <div className={`truncate font-display text-base font-semibold ${spinning ? "text-primary/70" : "text-text"}`}>{concept[key]}</div>
                    {key === "twist" && !spinning && twistHint(concept[key]!) && (
                      <div className="mt-0.5 truncate text-[11px] text-muted">{twistHint(concept[key]!)}</div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => clearSlot(key)}
                      aria-label="Clear slot"
                      title="Clear this slot"
                      className="grid h-9 w-9 place-items-center rounded-lg border border-border text-muted transition-colors hover:bg-surface-2 hover:text-text"
                    >
                      <Eraser className="h-4 w-4" />
                    </button>
                    <button onClick={() => toggleLock(key)} aria-label={isLocked ? "Unlock slot" : "Lock slot"} className={lockBtn(isLocked)}>
                      {isLocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => reroll(concept)}
          disabled={rolling}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 font-display font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.03] disabled:opacity-60"
        >
          <RotateCcw className={`h-4 w-4 ${rolling ? "animate-spin" : ""}`} />
          Reroll unlocked
        </button>

        {/* Secondary actions — quiet icon group */}
        <div className="flex items-center gap-1 rounded-xl border border-border bg-surface p-1">
          <ActionIcon label={allLocked ? "Unlock all" : "Lock all"} onClick={allLocked ? unlockAll : lockAll} active={allLocked}>
            {allLocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
          </ActionIcon>
          <ActionIcon label="Save idea" onClick={saveIdea}><Bookmark className="h-4 w-4" /></ActionIcon>
          <ActionIcon label="Copy pitch" onClick={copyPitch}><Copy className="h-4 w-4" /></ActionIcon>
          <ActionIcon label="Copy seed" onClick={copySeed}><Hash className="h-4 w-4" /></ActionIcon>
          <ActionIcon label="Customize roll" onClick={() => setShowCustomize((v) => !v)} active={showCustomize}>
            <SlidersHorizontal className="h-4 w-4" />
          </ActionIcon>
        </div>

        <button
          onClick={createPitch}
          title="Open a Pitch doc in Design Docs, prefilled from this concept"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-primary/10"
        >
          <FileText className="h-4 w-4 text-primary" />
          Create Pitch
        </button>
      </div>

      {showCustomize && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border bg-surface p-6">
          <p className="mb-4 text-sm text-muted">Choose which slots and values can roll. Apply, then reroll.</p>
          <JamSlotPicker />
          <button
            onClick={startFresh}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]"
          >
            <Dices className="h-4 w-4" />
            Apply &amp; reroll
          </button>
        </motion.div>
      )}

      {favorites.length > 0 && (
        <Favorites favorites={favorites} onLoad={loadConcept} onRemove={remove} onCopyAll={copyAllFavorites} onExport={exportFavorites} />
      )}
      {toastEl}
    </div>
  );
}

function ActionIcon({
  children,
  onClick,
  label,
  active = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  active?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={[
        "grid h-9 w-9 place-items-center rounded-lg transition-colors",
        active ? "bg-primary text-primary-fg" : "text-muted hover:bg-surface-2 hover:text-text",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function toolBtn(active: boolean): string {
  return [
    "inline-flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-colors",
    active ? "border-primary bg-primary/10 text-text" : "border-border bg-surface text-text hover:bg-surface-2",
  ].join(" ");
}

function lockBtn(active: boolean): string {
  return [
    "grid h-9 w-9 shrink-0 place-items-center rounded-lg border transition-colors",
    active ? "border-primary bg-primary text-primary-fg" : "border-border text-muted hover:bg-surface-2 hover:text-text",
  ].join(" ");
}

function NameBtn({
  children,
  onClick,
  label,
  disabled = false,
  active = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={[
        "grid h-7 w-7 place-items-center rounded-md border transition-colors disabled:opacity-30",
        active ? "border-primary bg-primary text-primary-fg" : "border-border text-muted hover:bg-surface-2 hover:text-text",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Favorites({
  favorites,
  onLoad,
  onRemove,
  onCopyAll,
  onExport,
}: {
  favorites: { id: string; concept: JamConcept }[];
  onLoad: (c: JamConcept) => void;
  onRemove: (id: string) => void;
  onCopyAll: () => void;
  onExport: () => void;
}) {
  return (
    <div className="mt-8">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-muted">
          Saved ideas ({favorites.length})
        </h3>
        <div className="flex gap-2">
          <button onClick={onCopyAll} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-muted transition-colors hover:bg-surface-2 hover:text-text">
            <ClipboardList className="h-3.5 w-3.5" /> Copy all
          </button>
          <button onClick={onExport} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-muted transition-colors hover:bg-surface-2 hover:text-text">
            <Download className="h-3.5 w-3.5" /> Export
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {favorites.map((f) => (
          <div key={f.id} className="flex items-center gap-3 rounded-xl border border-border bg-surface p-3">
            <div className="min-w-0 flex-1">
              <div className="truncate font-display font-semibold text-text">{f.concept.title}</div>
              <div className="truncate text-xs text-muted">{conceptSummary(f.concept)}</div>
            </div>
            <button onClick={() => onLoad(f.concept)} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-muted transition-colors hover:bg-surface-2 hover:text-text" aria-label="Load idea" title="Load">
              <FolderOpen className="h-4 w-4" />
            </button>
            <button onClick={() => onRemove(f.id)} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-muted transition-colors hover:bg-surface-2 hover:text-text" aria-label="Remove idea" title="Remove">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
